import { Link } from 'react-router-dom';
import { ArrowRight, Check, X } from 'lucide-react';
import { SITE, EXPERIENCE, MANIFESTO, IMG } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, SpotlightImage } from '../components/motion';
import { PageHero, CTABand } from '../components/chrome';

const crumbs = [{ label: 'Home', href: '/' }, { label: 'About' }];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About ToxGlobal Analytics: Analytical Equipment for Government & Regulated Labs"
        description="ToxGlobal Analytics is an analytical instrumentation and laboratory equipment company — procurement, installation, commissioning, validation support, and lifecycle support for government and regulated laboratories nationwide."
        path="/about"
        breadcrumbs={crumbs}
      />
      <PageHero
        eyebrow="About"
        lines="A laboratory equipment company — not a testing company."
        sub="ToxGlobal Analytics provides analytical instrumentation, equipment procurement, installation, commissioning, maintenance coordination, and lifecycle support for government laboratories, research facilities, and regulated commercial laboratories."
        breadcrumbs={crumbs}
        image={IMG.hero}
        imageAlt="ToxGlobal Analytics laboratory environment"
      />

      <section className="section-pad" data-testid="about-positioning">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div className="border border-emerald-200 bg-emerald-50/60 p-8 h-full" data-testid="about-is">
              <h2 className="display text-2xl text-ink flex items-center gap-3"><Check size={20} className="text-emerald-600" /> What ToxGlobal is</h2>
              <ul className="mt-5 space-y-3 text-sm text-slate-700 leading-relaxed">
                <li>An analytical equipment supplier with federal procurement capability — SAM.gov Active, UEI {SITE.uei}, CAGE {SITE.cage}.</li>
                <li>An installation and commissioning coordinator for analytical systems in government and regulated facilities.</li>
                <li>A lifecycle partner: validation support, calibration, preventive maintenance, repair coordination, parts, and upgrades.</li>
                <li>A prime or subcontractor for analytical-equipment scopes, nationwide.</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.07}>
            <div className="border border-slate-300 bg-white p-8 h-full" data-testid="about-is-not">
              <h2 className="display text-2xl text-ink flex items-center gap-3"><X size={20} className="text-slate-500" /> What ToxGlobal is not</h2>
              <ul className="mt-5 space-y-3 text-sm text-slate-700 leading-relaxed">
                <li>A laboratory testing or consumer diagnostics company — ToxGlobal supplies and supports instruments; it does not run patient tests.</li>
                <li>A manufacturer. Equipment is sourced through OEM and authorized-distributor channels, with authorization claimed only where documented.</li>
                <li>A regional dealer. Coverage is nationwide, by design.</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white section-pad hairline-t" data-testid="about-model">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">Operating model</p>
            <h2 className="display text-3xl sm:text-5xl text-ink mb-10 max-w-3xl">Four chapters, one accountable contractor</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {MANIFESTO.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.06} className="bg-white">
                <div className="p-7 h-full">
                  <p className="font-mono text-sm text-signal tabular">{c.n}</p>
                  <h3 className="display text-xl text-ink mt-3">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="about-experience">
        <div className="container-x grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
          <div>
            <Reveal>
              <p className="eyebrow text-signal mb-3">Experience</p>
              <h2 className="display text-3xl sm:text-4xl text-ink">Where we've worked</h2>
              <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
                ToxGlobal publishes only substantiated past-performance statements; contract-specific details are available on
                request during teaming and proposal processes.
              </p>
            </Reveal>
            <div className="mt-8 border-t border-slate-200">
              {EXPERIENCE.map((x, i) => (
                <Reveal key={x.org} delay={i * 0.05}>
                  <div className="py-5 border-b border-slate-200">
                    <h3 className="font-display font-semibold text-ink">{x.org}</h3>
                    <p className="mt-1 text-sm text-slate-600">{x.detail}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.08}>
            <SpotlightImage src={IMG.installTech} alt="Analytical instrument installation and support coordination" ratio="aspect-[4/5]" />
            <div className="mt-8 border border-slate-300 bg-white p-7">
              <h3 className="display text-xl text-ink">Coverage & service targets</h3>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                <li>Nationwide coverage — remote support in all 50 states; on-site installation and validation by arrangement.</li>
                <li>Typical installation window: 10–15 business days after delivery (product-dependent).</li>
                <li>Remote triage target: next business day. On-site target: 3–5 business days.</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Work with a supplier who stays past delivery."
        sub="Request a quote, send an RFQ, or discuss a requirement with the team behind the instruments."
      />
    </div>
  );
}
